import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  RefreshCw,
  LogOut,
  Search,
  Download,
  Trash2,
  Check,
  Mail,
  Phone,
  Loader2,
  Filter,
} from "lucide-react";
import { listInquiries, updateStatus, deleteInquiry } from "./api";
import { LOGO_URL } from "../data/site";

function fmtDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function toCSV(rows) {
  const headers = [
    "id",
    "created_at",
    "status",
    "name",
    "email",
    "phone",
    "budget",
    "project_interest",
    "message",
  ];
  const escape = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v).replace(/"/g, '""');
    return `"${s}"`;
  };
  const lines = [headers.join(",")];
  rows.forEach((r) => lines.push(headers.map((h) => escape(r[h])).join(",")));
  return lines.join("\n");
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const email = localStorage.getItem("udp_admin_email") || "admin";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all"); // all | new | contacted
  const [busy, setBusy] = useState(null); // inquiry id being mutated

  const load = async () => {
    setLoading(true);
    try {
      const data = await listInquiries();
      setRows(data || []);
    } catch (err) {
      if (err?.response?.status !== 401) toast.error("Failed to load inquiries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("udp_admin_token")) {
      navigate("/admin/login");
      return;
    }
    load();
     
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter !== "all" && (r.status || "new") !== filter) return false;
      if (!q) return true;
      return [r.name, r.email, r.phone, r.project_interest, r.budget, r.message]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [rows, query, filter]);

  const stats = useMemo(() => {
    const total = rows.length;
    const newC = rows.filter((r) => (r.status || "new") === "new").length;
    const contacted = total - newC;
    const today = rows.filter((r) => {
      const d = new Date(r.created_at);
      const now = new Date();
      return d.toDateString() === now.toDateString();
    }).length;
    return { total, newC, contacted, today };
  }, [rows]);

  const toggleStatus = async (row) => {
    const next = (row.status || "new") === "new" ? "contacted" : "new";
    setBusy(row.id);
    try {
      const updated = await updateStatus(row.id, next);
      setRows((rs) => rs.map((r) => (r.id === row.id ? updated : r)));
      toast.success(`Marked as ${next}`);
    } catch {
      toast.error("Failed to update");
    } finally {
      setBusy(null);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete inquiry from ${row.name}?`)) return;
    setBusy(row.id);
    try {
      await deleteInquiry(row.id);
      setRows((rs) => rs.filter((r) => r.id !== row.id));
      toast.success("Inquiry deleted");
    } catch {
      toast.error("Failed to delete");
    } finally {
      setBusy(null);
    }
  };

  const exportCsv = () => {
    const blob = new Blob([toCSV(filtered)], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `udp-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const logout = () => {
    localStorage.removeItem("udp_admin_token");
    localStorage.removeItem("udp_admin_email");
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-[#050a18]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="sticky top-0 z-30 glass-strong border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="UDP" className="w-10 h-10 rounded-full ring-1 ring-[#c5a059]/40" />
            <div>
              <div className="text-[10px] tracking-[0.3em] text-[#c5a059] uppercase">Admin Dashboard</div>
              <div className="font-serif-display text-lg">Universal Dream Properties</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-xs text-white/50" data-testid="admin-current-user">{email}</span>
            <button
              onClick={load}
              className="glass px-3 py-2 rounded-lg text-xs flex items-center gap-2 hover:text-[#c5a059]"
              data-testid="admin-refresh-btn"
            >
              <RefreshCw size={14} /> Refresh
            </button>
            <button
              onClick={logout}
              className="btn-outline-gold px-3 py-2 rounded-lg text-xs flex items-center gap-2"
              data-testid="admin-logout-btn"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="admin-stats">
          {[
            { label: "Total Leads", value: stats.total, key: "total" },
            { label: "New", value: stats.newC, key: "new" },
            { label: "Contacted", value: stats.contacted, key: "contacted" },
            { label: "Today", value: stats.today, key: "today" },
          ].map((s) => (
            <div key={s.key} className="glass rounded-xl p-5" data-testid={`stat-${s.key}`}>
              <div className="font-serif-display text-3xl text-[#c5a059]">{s.value}</div>
              <div className="text-[10px] tracking-[0.3em] text-white/50 uppercase mt-1">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Toolbar */}
        <div className="flex flex-col md:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={16} />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, email, phone, project…"
              className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-4 py-3 text-sm focus:border-[#c5a059] outline-none"
              data-testid="admin-search-input"
            />
          </div>
          <div className="flex items-center gap-2 glass rounded-lg px-3">
            <Filter size={14} className="text-white/40" />
            {["all", "new", "contacted"].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-2 rounded-md text-[11px] uppercase tracking-widest transition-colors ${
                  filter === f ? "bg-[#c5a059] text-[#0a1128]" : "text-white/70 hover:text-white"
                }`}
                data-testid={`filter-${f}`}
              >
                {f}
              </button>
            ))}
          </div>
          <button
            onClick={exportCsv}
            disabled={!filtered.length}
            className="btn-outline-gold px-4 py-3 rounded-lg text-xs tracking-widest flex items-center gap-2 disabled:opacity-40"
            data-testid="admin-export-csv"
          >
            <Download size={14} /> EXPORT CSV
          </button>
        </div>

        {/* Table */}
        <div className="glass rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-inquiries-table">
              <thead className="bg-white/5 text-[10px] tracking-[0.25em] uppercase text-white/50">
                <tr>
                  <th className="text-left px-5 py-4">When</th>
                  <th className="text-left px-5 py-4">Contact</th>
                  <th className="text-left px-5 py-4">Interest</th>
                  <th className="text-left px-5 py-4">Budget</th>
                  <th className="text-left px-5 py-4">Message</th>
                  <th className="text-left px-5 py-4">Status</th>
                  <th className="text-right px-5 py-4">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="text-center py-16 text-white/50">
                      <Loader2 className="animate-spin inline mr-2" size={16} /> Loading leads…
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-16 text-white/40" data-testid="admin-empty-state">
                      No leads yet. New form submissions will appear here.
                    </td>
                  </tr>
                ) : (
                  filtered.map((r) => (
                    <tr key={r.id} className="hover:bg-white/[0.03]" data-testid={`inquiry-row-${r.id}`}>
                      <td className="px-5 py-4 whitespace-nowrap text-white/70">{fmtDate(r.created_at)}</td>
                      <td className="px-5 py-4">
                        <div className="font-semibold">{r.name}</div>
                        <a href={`mailto:${r.email}`} className="flex items-center gap-1 text-xs text-white/60 hover:text-[#c5a059] mt-1">
                          <Mail size={11} /> {r.email}
                        </a>
                        <a href={`tel:${r.phone}`} className="flex items-center gap-1 text-xs text-white/60 hover:text-[#c5a059]">
                          <Phone size={11} /> {r.phone}
                        </a>
                      </td>
                      <td className="px-5 py-4 text-white/80">{r.project_interest || "—"}</td>
                      <td className="px-5 py-4 text-white/80 whitespace-nowrap">{r.budget || "—"}</td>
                      <td className="px-5 py-4 text-white/70 max-w-xs">
                        <div className="line-clamp-3 text-xs leading-relaxed">{r.message || "—"}</div>
                      </td>
                      <td className="px-5 py-4">
                        <span
                          className={`px-2.5 py-1 rounded-full text-[10px] tracking-widest uppercase ${
                            (r.status || "new") === "new"
                              ? "bg-[#c5a059]/15 text-[#c5a059]"
                              : "bg-green-500/15 text-green-400"
                          }`}
                        >
                          {r.status || "new"}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => toggleStatus(r)}
                            disabled={busy === r.id}
                            className="p-2 rounded-md hover:bg-white/5 text-white/70 hover:text-green-400"
                            title="Toggle contacted"
                            data-testid={`toggle-${r.id}`}
                          >
                            <Check size={15} />
                          </button>
                          <button
                            onClick={() => remove(r)}
                            disabled={busy === r.id}
                            className="p-2 rounded-md hover:bg-white/5 text-white/70 hover:text-red-400"
                            title="Delete"
                            data-testid={`delete-${r.id}`}
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
}
